import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoAdd } from "react-icons/io5";

import CareOfTable from "./components/CareOfTable";
import { createCareOf, deleteCareOf, getCareOfList } from "./careOfApi";

const CareOfManagement = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    country: "",
    state: "",
    district: "",
    source: "",
    status: "",
  });
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleFiltersChange = (nextFilters) => {
    setPage(1);
    setFilters(nextFilters);
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters({
      search: "",
      country: "",
      state: "",
      district: "",
      source: "",
      status: "",
    });
  };

  const fetchCareOfList = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getCareOfList({ page, limit, filters });
      setTableData(result.items);
      setTotal(result.total);
    } catch (listError) {
      setError(listError?.response?.data?.message || "Failed to fetch care of list");
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    fetchCareOfList();
  }, [fetchCareOfList]);

  const handleCreateCareOf = async (newCareOf) => {
    try {
      await createCareOf(newCareOf);
      showToast("CareOf created successfully", "success");
      setPage(1);
      await fetchCareOfList();
    } catch (createError) {
      const apiErrors = createError?.response?.data?.errors;
      const hasFieldErrors = Array.isArray(apiErrors) && apiErrors.length > 0;
      if (!hasFieldErrors) {
        showToast(createError?.response?.data?.message || "Failed to create care of", "error");
      }
      throw createError;
    }
  };

  const handleDeleteCareOf = async (careOfId) => {
    try {
      await deleteCareOf(careOfId);
      showToast("CareOf deleted successfully", "success");

      if (tableData.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
        return;
      }

      await fetchCareOfList();
    } catch (deleteError) {
      showToast(deleteError?.response?.data?.message || "Failed to delete care of", "error");
      return false;
    }
  };

  useEffect(() => {
    if (!isAddModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAddModalOpen]);

  useEffect(() => {
    if (!toast.show) return undefined;
    const timer = setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
    return () => clearTimeout(timer);
  }, [toast.show]);

  return (
    <div>
      {toast.show && (
        <div
          className={`fixed right-6 top-20 z-[60] rounded-lg px-4 py-3 text-sm font-semibold shadow-md backdrop-blur-sm ${toast.type === "error"
            ? "border border-red-400/70 bg-red-500/20 text-red-700"
            : "border border-green-400/70 bg-green-500/20 text-green-700"
            }`}
        >
          {toast.message}
        </div>
      )}

      <div className="mb-4 mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">CareOf Management</h2>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white transition-colors hover:bg-brand-600 sm:w-auto"
        >
          <IoAdd className="h-5 w-5" />
          Add CareOf
        </button>
      </div>

      <div className="mt-5 grid h-full grid-cols-1 gap-5 md:grid-cols-1">
        <CareOfTable
          tableData={tableData}
          isLoading={loading}
          error={error}
          filters={filters}
          total={total}
          onFilterChange={handleFilterChange}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          currentPage={page}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
          isAddModalOpen={isAddModalOpen}
          onCloseAddModal={() => setIsAddModalOpen(false)}
          onCreateCareOf={handleCreateCareOf}
          onDeleteCareOf={handleDeleteCareOf}
          showToast={showToast}
        />
      </div>
    </div>
  );
};

export default CareOfManagement;
